import React, { useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { CalendarPlus, Eye, EyeOff, FileCheck2, Hourglass, MessageCircleHeart, MessageSquareText, Phone, ScanSearch, Star, UserCheck, UserPlus, Zap } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { isOwnedBy } from '../onboardingSupport/ownership';
import { LegacyTag, isLegacy } from '../scheduling/MeetingActions';
import ActivityMark from './ActivityMark';
import HotSeatMark from './HotSeatMark';
import ScoreboardCard from '../workspace/ScoreboardCard';
import {
  Counter,
  FilterChip,
  NotesButton,
  PANEL,
  PinStar,
  PlainHead,
  ScheduleCard,
  SortHead,
  TodayCard,
  TodoMark,
  firstOf,
  initialsOf,
  sameDay,
  todosByPerson,
  yourMoveWords,
  ClosedTodayNote,
  FILTERED_PANEL,
  FilteredPill,
} from '../workspace/DeskKit';
import {
  useAddTodo,
  useDeleteTodo,
  useOnboardingTodos,
  useSaveOnboardingDisplayPrefs,
  useSetPinned,
  useUpdateTodo,
} from '../../../services/clientOnboarding';

// The Client Onboarding desk (Nic 2026-10-11: "one clean professional
// workspace"). No lanes and no guided steps. Four numbers that are filters
// (New, Needs first meeting, Booked, No movement 7+ days), one list of every
// client with where they are as a plain word and their last movement (the
// one aging signal, server lib/clientMovement), and a rail with the meeting
// schedule and Today's to-dos. Nothing here locks anything: clicking a
// client opens the same Workspace for everyone.


// Each suggestion's kind (its autoKey prefix): an icon and a tint.
const KIND_OF = {
  call: { icon: Phone, tile: 'bg-sky-50 text-sky-600 dark:!bg-sky-400/10 dark:text-sky-300' },
  new: { icon: UserPlus, tile: 'bg-teal-50 text-teal-600 dark:!bg-teal-400/10 dark:text-teal-300' },
  // A missed hot seat (Nic 2026-10-23).
  callback: { icon: Phone, tile: 'bg-orange-50 text-orange-600 dark:!bg-orange-400/10 dark:text-orange-300' },
  // Waiting on VA.gov (Nic 2026-10-23).
  nudge: { icon: MessageSquareText, tile: 'bg-orange-50 text-orange-600 dark:!bg-orange-400/10 dark:text-orange-300' },
  quiet: { icon: Hourglass, tile: 'bg-amber-50 text-amber-600 dark:!bg-amber-400/10 dark:text-amber-300' },
  // Reached but no first call booked (Nic 2026-10-23).
  book: { icon: CalendarPlus, tile: 'bg-indigo-50 text-indigo-600 dark:!bg-indigo-400/10 dark:text-indigo-300' },
  // The VA check is back and needs confirming (Nic 2026-10-23).
  confirmsa: { icon: ScanSearch, tile: 'bg-violet-50 text-violet-600 dark:!bg-violet-400/10 dark:text-violet-300' },
  // Waiting on PTRs: they sent their records in (Nic 2026-10-26).
  ptrreview: { icon: FileCheck2, tile: 'bg-pink-50 text-pink-600 dark:!bg-pink-400/10 dark:text-pink-300' },
  // Waiting on PTRs, every 14 days (Nic 2026-10-26).
  ptrnudge: { icon: MessageSquareText, tile: 'bg-pink-50 text-pink-600 dark:!bg-pink-400/10 dark:text-pink-300' },
};

// Why a Confirm service agreement suggestion is there, from its VA check.
const SA_WHY = {
  match: 'The VA check matches',
  rating_only_match: 'The VA check matches the rating',
  mismatch: 'The VA check found a difference',
  unclear: "The VA check couldn't pin it down",
  no_answer: "The bot couldn't reach the VA",
  cannot_call: "The bot couldn't call",
};

const WORKING = ['vaGov', 'needsBooking', 'booked', 'records', 'ptrs'];

const callTime = (at, now) =>
  sameDay(at, now)
    ? `today ${moment(at).format('h:mm A')}`
    : sameDay(at, moment(now).add(1, 'day'))
      ? `tomorrow ${moment(at).format('h:mm A')}`
      : moment(at).format('ddd, MMM D h:mm A');

// Where they are, as one word (no lane, no lock).
const STAGE = {
// One color each (Nic 2026-10-12); Needs first meeting is the only gray.
  new: { label: 'New', cls: 'bg-teal-500/10 text-teal-700 dark:text-teal-300' },
  meet: { label: 'Needs first meeting', cls: 'bg-slate-100 text-slate-700 dark:!bg-white/10 dark:text-slate-200' },
  booked: { label: 'Booked', cls: 'bg-sky-500/10 text-sky-700 dark:text-sky-300' },
  vaGov: { label: 'Setting up VA.gov', cls: 'bg-orange-500/10 text-orange-700 dark:text-orange-300' },
  records: { label: 'Gathering records', cls: 'bg-purple-500/10 text-purple-700 dark:text-purple-300' },
  // Everything is in but their private treatment records (Nic 2026-10-26).
  ptrs: { label: 'Waiting on PTRs', cls: 'bg-pink-500/10 text-pink-700 dark:text-pink-300' },
  ready: { label: 'Ready for CM', cls: 'bg-green-500/10 text-green-700 dark:text-green-300' },
};
const stageOf = (c) => {
  if (c.column === 'readyForCm') return 'ready';
  if (c.column === 'vaGov') return 'vaGov';
  if (c.column === 'records') return 'records';
  if (c.column === 'ptrs') return 'ptrs';
  if (c.column === 'booked') return 'booked';
  // New: no call, text or email from the app yet (a hot seat that
  // connected is past this), and not someone carried over at launch.
  return c.lastTouchAt || c.carriedAtLaunch ? 'meet' : 'new';
};
// Their call, until it ends (Nic 2026-10-27: one that's over already counts
// as happened, so it leaves the to-dos, Next and Booked; the Schedule keeps
// it as done, and They missed it stays in their Workspace for the day).
const meetingEnd = (m) => (m.endTime ? moment(m.endTime) : moment(m.startTime).add(30, 'minutes'));
export const upcomingCall = (c, now) =>
  c.meeting?.startTime && meetingEnd(c.meeting).isAfter(moment(now)) ? c.meeting : null;
// Who the call is with when it is not their specialist (Nic 2026-10-30, for
// the launch move: a call booked with Austin before the client became
// Kit's). Their first name, else null. By user id when both sides carry
// one; by name otherwise (Calendly often stores only a first name).
export const callWith = (c, call) => {
  const host = String(call?.hostName || '').trim();
  if (!host) return null;
  const first = host.split(/\s+/)[0];
  if (call.hostUserId && c.ownerUserId) return call.hostUserId === c.ownerUserId ? null : first;
  const owner = String(c.ownerName || '')
    .trim()
    .toLowerCase();
  const h = host.toLowerCase();
  return h === owner || (!!owner && h === owner.split(/\s+/)[0]) ? null : first;
};
const withWords = (c, call) => {
  const who = callWith(c, call);
  return who ? ` with ${who}` : '';
};

// Last movement, Pipedrive's "rotting": green under 7 days, amber 7 to 13,
// red from 14.
const moveTone = (d) =>
  d == null ? 'text-slate-400' : d >= 14 ? 'text-rose-600 dark:text-rose-300' : d >= 7 ? 'text-amber-600 dark:text-amber-300' : 'text-emerald-600 dark:text-emerald-300';
const moveDot = (d) => (d == null ? 'bg-slate-300' : d >= 14 ? 'bg-rose-500' : d >= 7 ? 'bg-amber-500' : 'bg-emerald-500');
const moveText = (d) => (d == null ? 'None' : d === 0 ? 'Today' : d === 1 ? '1 day' : `${d} days`);

// What's next for them, in a few words.
const nextOf = (c, stage, labels, now) => {
  const call = upcomingCall(c, now);
  // A Calendly meeting from before launch carries its tag (Nic 2026-10-27).
  if (call) return { text: `Call ${callTime(call.startTime, now)}${withWords(c, call)}`, strong: sameDay(call.startTime, now), legacy: isLegacy(call) };
  // A missed hot seat they haven't reached since, whatever the stage.
  if (c.hotSeatCallBack) return { text: 'Call them back', strong: true };
  if (stage === 'new') return { text: 'Reach out' };
  if (stage === 'meet') return { text: 'Book their first call' };
  if (stage === 'vaGov') return { text: 'Waiting on them for VA.gov' };
  // Still theirs until a Claims Manager is assigned (Nic 2026-10-26).
  if (stage === 'ready') return { text: c.carriedOver ? 'Carried over · CM Assignment' : 'Waiting on CM Assignment' };
  if (stage === 'ptrs') return c.ptrUploads ? { text: 'Review their PTRs', strong: true } : { text: 'Waiting on their PTRs' };
  const name = (k) => labels[k] || k;
  const waiting = c.waitingOn || [];
  if (waiting.length) return { text: `Waiting on ${waiting.slice(0, 2).map(name).join(', ')}${waiting.length > 2 ? ` +${waiting.length - 2}` : ''}` };
  const needed = (c.missing || []).filter((k) => !waiting.includes(k));
  if (needed.length) return { text: `Still needed: ${needed.slice(0, 2).map(name).join(', ')}${needed.length > 2 ? ` +${needed.length - 2}` : ''}` };
  return { text: 'Everything is in' };
};

// ---- The desk -------------------------------------------------------------------

const SORTS = {
  attention: null,
  name: (a, b) => String(a.name || '').localeCompare(String(b.name || '')),
  movement: (a, b) => (b.movementDays ?? -1) - (a.movementDays ?? -1),
  checklist: (a, b) => (a.progress?.done ?? 0) - (b.progress?.done ?? 0),
};

// The board's columns as desk rows (each with its column and stage).
export const workingRowsOf = (shown) =>
  WORKING.flatMap((col) => (shown[col] || []).map((c) => ({ ...c, column: col }))).map((c) => ({
    ...c,
    stage: stageOf(c),
  }));

// Waiting on PTRs' reminder (Nic 2026-10-26): every 14 days with nothing
// new. The latest of when they started waiting, their last PTR upload and
// the last text to them; null when they aren't Waiting on PTRs.
export const PTR_NUDGE_DAYS = 14;
export const ptrNudgeAnchorOf = (c) => {
  if (c.stage !== 'ptrs') return null;
  const times = [c.ptrsSince, c.ptrLastUploadAt, c.ptrsNudgedAt]
    .filter(Boolean)
    .map((t) => new Date(t).getTime())
    .filter(Number.isFinite);
  return times.length ? new Date(Math.max(...times)) : null;
};

// Does the row want a nudge (Nic 2026-10-27, like Sales' Signing Up: the
// icon goes once the nudge went out, and comes back when one is due again)?
//   Setting up VA.gov   none sent since they started waiting, or within 3
//                       days of Stalled (the Today rail's own rule)
//   Waiting on PTRs     none sent since they started waiting or last uploaded,
//                       or 14 days with nothing new (PTR_NUDGE_DAYS)
export const nudgeWanted = (c, now = new Date(), stallDays = 14) => {
  if (c.stage === 'vaGov') {
    const since = c.vaGov?.waitingSince ? new Date(c.vaGov.waitingSince).getTime() : 0;
    const sent = c.vaGov?.nudgedAt ? new Date(c.vaGov.nudgedAt).getTime() : null;
    if (!sent || sent < since) return true;
    return Math.max(0, stallDays - (c.movementDays ?? 0)) <= 3;
  }
  if (c.stage === 'ptrs') {
    const started = Math.max(
      ...[c.ptrsSince, c.ptrLastUploadAt].filter(Boolean).map((t) => new Date(t).getTime()),
      0,
    );
    const sent = c.ptrsNudgedAt ? new Date(c.ptrsNudgedAt).getTime() : null;
    if (!sent || sent < started) return true;
    const anchor = ptrNudgeAnchorOf(c);
    return !!anchor && moment(now).startOf('day').diff(moment(anchor).startOf('day'), 'days') >= PTR_NUDGE_DAYS;
  }
  return false;
};

// Is this meeting their first one? (Nic 2026-10-27: a later call can be for
// anything, so only the first is named.) Before the first call it's the one
// booked; after, only the booking that counted as it (firstCallBookingId),
// so the first meeting still says First call once it moved them on. Someone
// past their first call with no stamp (carried over at launch) has none.
export const isFirstMeeting = (c, call) =>
  c.firstCallAt
    ? !!call?.calendlyBookingId && call.calendlyBookingId === c.firstCallBookingId
    : !['records', 'ptrs', 'ready'].includes(c.stage);

// Today's suggestions (the viewer's own clients): calls today, call-backs,
// new to reach out to, a VA.gov nudge, and anyone close to Stalled. One
// function for the desk's Today card and the Clients tab's number (Nic
// 2026-10-23: a tab number means you have something to do).
// Each suggestion carries `done` (Nic 2026-11-03), read from the fact it is
// about, so it checks itself off and stays on the list for the day:
//   First call / Call   the call is over (a no-show never sits on `meeting`)
//   Call back           a connected call with them since the missed hot seat
//                       (hotSeatCalledBackAt)
//   Reach out           reached today (firstTouchAt)
//   Book a first call   a call was booked today (meeting.bookedAt)
//   Confirm SA          confirmed today (saConfirmedAt)
//   Review PTRs         Got it ticked today (ptrReviewedAt)
//   Nudge (VA.gov)      the nudge went out today (vaGov.nudgedAt)
//   Nudge (PTRs)        the nudge went out today (ptrsNudgedAt)
//   Check in            a call, text or email to them today (lastTouchAt)
export const suggestionsFor = (working, viewerId, now, stallDays = 14) => {
  const mine = working.filter((c) => isOwnedBy(c.ownerUserId, viewerId));
  // Setting up VA.gov is THEIR step (Nic 2026-10-25): no to-dos for their
  // specialist (no reach out, service agreement or check-in) until they've
  // done it. They still show on the list; only a call already on the
  // calendar for today, and the nudge near Stalled (below), are suggested.
  const mineOnly = mine.filter((c) => c.stage !== 'vaGov');
  const day = moment(now).format('YYYY-MM-DD');
  const today = (at) => !!at && sameDay(at, now);
  const byKey = new Map();
  // A done reading wins over a live ask with the same key.
  const put = (x) => {
    const had = byKey.get(x.key);
    if (!had || (x.done && !had.done)) byKey.set(x.key, x);
  };
  mine.forEach((c) => {
    // Their call today: ahead, or over (met: a no-show never sits on the card).
    const call = c.meeting?.startTime && sameDay(c.meeting.startTime, now) ? c.meeting : null;
    if (call) {
      put({
        key: `call:${c.onboardingId}:${moment(call.startTime).format('YYYY-MM-DD')}`,
        personId: c.onboardingId,
        text: `${isFirstMeeting(c, call) ? 'First call' : 'Call'}: ${c.name}`,
        why: `${moment(call.startTime).format('h:mm A')}${withWords(c, call)}`,
        order: 0,
        at: call.startTime,
        done: !upcomingCall(c, now),
      });
    }
  });
  mineOnly.forEach((c) => {
    // A missed hot seat (Nic 2026-10-23, from RingCentral, live): until their
    // specialist reaches them, whatever stage they're in.
    if (c.hotSeatCallBack) {
      put({ key: `callback:${c.onboardingId}`, personId: c.onboardingId, text: `Call ${c.name} back`, why: "Their hot seat didn't connect", order: 1, done: false });
    } else if (today(c.hotSeatCalledBackAt)) {
      put({ key: `callback:${c.onboardingId}`, personId: c.onboardingId, text: `Call ${c.name} back`, why: `Reached ${moment(c.hotSeatCalledBackAt).format('h:mm A')}`, order: 1, done: true });
    }
    if (c.stage === 'new') {
      put({ key: `new:${c.onboardingId}`, personId: c.onboardingId, text: `Reach out to ${c.name}`, why: 'New client', order: 1, done: false });
    } else if (today(c.firstTouchAt)) {
      put({ key: `new:${c.onboardingId}`, personId: c.onboardingId, text: `Reach out to ${c.name}`, why: `Reached ${moment(c.firstTouchAt).format('h:mm A')}`, order: 1, done: true });
    }
    if (c.stage === 'meet' && !upcomingCall(c, now)) {
      // Reached, but no first call on the calendar (Nic 2026-10-23: they
      // had no suggestion at all until they neared Stalled).
      put({ key: `book:${c.onboardingId}:${day}`, personId: c.onboardingId, text: `Book a first call with ${c.name}`, why: 'No first call booked yet', order: 1, done: false });
    }
    // Only where the ask lives (review 2026-11-03): their first call, not
    // a follow-up booked for someone already past it.
    if (today(c.meeting?.bookedAt) && (c.stage === 'meet' || isFirstMeeting(c, c.meeting))) {
      put({
        key: `book:${c.onboardingId}:${day}`,
        personId: c.onboardingId,
        text: `Book a first call with ${c.name}`,
        why: `Booked for ${moment(c.meeting.startTime).format('ddd h:mm A')}`,
        order: 1,
        done: true,
      });
    }
  });
  mineOnly.forEach((c) => {
    // Confirm service agreement (Nic 2026-10-23): the VA check is back (or
    // gave up) and nobody has confirmed it with them yet. Clients who signed
    // before the check get none.
    const sa = c.saCheck;
    const key = `confirmsa:${c.onboardingId}:${day}`;
    if (today(c.saConfirmedAt)) {
      put({ key, personId: c.onboardingId, text: `Confirm ${c.name}'s service agreement`, why: 'Confirmed', order: 1, done: true });
      return;
    }
    if (c.saConfirmed || !sa || sa.accepted || ['queued', 'calling'].includes(sa.status)) return;
    put({
      key,
      personId: c.onboardingId,
      text: `Confirm ${c.name}'s service agreement`,
      why: SA_WHY[sa.status === 'done' ? sa.verdict : sa.status] || 'The VA check is back',
      order: 1,
      done: false,
    });
  });
  mineOnly.forEach((c) => {
    // Waiting on PTRs and they sent some in (Nic 2026-10-26): look at them and
    // tick Got it. A new upload asks again.
    if (c.stage === 'ptrs' && c.ptrUploads) {
      put({
        key: `ptrreview:${c.onboardingId}:${c.ptrUploads}`,
        personId: c.onboardingId,
        text: `Review ${c.name}'s PTRs`,
        why: `${c.ptrUploads} file${c.ptrUploads === 1 ? '' : 's'} sent: tick Got it if they're all here`,
        order: 1,
        done: false,
      });
    } else if (today(c.ptrReviewedAt)) {
      put({ key: `ptrreview:${c.onboardingId}:${c.ptrUploads || 0}`, personId: c.onboardingId, text: `Review ${c.name}'s PTRs`, why: 'Got it', order: 1, done: true });
    }
  });
  mine.forEach((c) => {
    // Setting up VA.gov: one nudge, only when they're within 3 days of
    // Stalled (Nic 2026-10-25). Sending it is our activity, not a sign of
    // life (Nic 2026-10-29): only their reply or their VA.gov holds them
    // off Stalled. Nothing else for VA.gov until they've done it.
    if (c.stage !== 'vaGov') return;
    const left = Math.max(0, stallDays - (c.movementDays ?? 0));
    const nudged = today(c.vaGov?.nudgedAt);
    if (left > 3 && !nudged) return;
    put({
      key: `nudge:${c.onboardingId}:${day}`,
      personId: c.onboardingId,
      text: `Nudge ${c.name} about VA.gov`,
      why: nudged ? `Nudged ${moment(c.vaGov.nudgedAt).format('h:mm A')}` : `Waiting on VA.gov${left ? `: Stalled in ${left} day${left === 1 ? '' : 's'}` : ': Stalled tonight'}`,
      order: 2,
      done: nudged,
    });
  });
  mine.forEach((c) => {
    // Waiting on PTRs never stalls (Nic 2026-10-26): a nudge every 14 days
    // with nothing new, counted from the latest of when they started
    // waiting, their last PTR upload and the last text to them.
    if (today(c.ptrsNudgedAt)) {
      put({ key: `ptrnudge:${c.onboardingId}:${day}`, personId: c.onboardingId, text: `Nudge ${c.name} about their PTRs`, why: `Nudged ${moment(c.ptrsNudgedAt).format('h:mm A')}`, order: 2, done: true });
      return;
    }
    const anchor = ptrNudgeAnchorOf(c);
    if (!anchor) return;
    const days = moment(now).startOf('day').diff(moment(anchor).startOf('day'), 'days');
    if (days < PTR_NUDGE_DAYS) return;
    put({
      key: `ptrnudge:${c.onboardingId}:${moment(anchor).format('YYYY-MM-DD')}`,
      personId: c.onboardingId,
      text: `Nudge ${c.name} about their PTRs`,
      why: `Nothing new on their PTRs in ${days} days`,
      order: 2,
      done: false,
    });
  });
  mineOnly.forEach((c) => {
    // PTRs have their own nudge (above), never a second check-in. A call,
    // text or email to them today is the check-in (our activity is not
    // movement, so the ask stays and reads done).
    if (c.stage === 'ptrs') return;
    if ((c.movementDays ?? 0) >= stallDays - 3 && !upcomingCall(c, now)) {
      const left = Math.max(0, stallDays - c.movementDays);
      const touched = today(c.lastTouchAt);
      put({
        key: `quiet:${c.onboardingId}:${day}`,
        personId: c.onboardingId,
        text: `Check in with ${c.name}`,
        why: touched ? `Reached out ${moment(c.lastTouchAt).format('h:mm A')}` : `No movement in ${c.movementDays} days${left ? `: Stalled in ${left} day${left === 1 ? '' : 's'}` : ': Stalled tonight'}`,
        order: 2,
        done: touched,
      });
    }
  });
  return [...byKey.values()].sort((a, b) => a.order - b.order || new Date(a.at || 0) - new Date(b.at || 0));
};

// The Clients tab's number: how many of your own clients have something
// for you today that you haven't checked off (todos: the Today card's own
// list; a checked suggestion is a done todo carrying its key).
// The clients behind it (working rows in), shared with the desk's Your
// move today counter so the two always agree (Nic 2026-10-23).
export const clientIdsNeedingYou = (working, viewerId, now, stallDays = 14) =>
  new Set(
    suggestionsFor(working, viewerId, now, stallDays)
      .filter((x) => !x.done)
      .map((x) => x.personId),
  );
export const clientsNeedingYou = (shown, viewerId, now, stallDays = 14) =>
  clientIdsNeedingYou(workingRowsOf(shown), viewerId, now, stallDays).size;

// Moved to CM today (Nic 2026-10-26, like Sales' Signed today): who got their
// Claims Manager today. Anyone still waiting on one is on the
// list (All, and the Ready for CM chip). Each one opens their Workspace.
export const MovedToCm = ({ assigned = [], showOwner = false, onOpen }) => {
  const row = (c, sub, tone) => (
    <button
      key={c.onboardingId}
      type="button"
      onClick={() => onOpen(c.onboardingId)}
      className={cn(
        'flex w-full flex-col rounded-lg px-3 py-2 text-left outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60',
        tone === 'waiting'
          ? 'bg-emerald-50 hover:bg-emerald-100/70 dark:!bg-emerald-500/10 dark:hover:!bg-emerald-500/15'
          : 'bg-slate-50 hover:bg-slate-100 dark:!bg-white/[0.04] dark:hover:!bg-white/[0.07]',
      )}
    >
      <span className="flex w-full items-center gap-2">
        <UserCheck size={14} className={cn('shrink-0', tone === 'waiting' ? 'text-emerald-600 dark:text-emerald-300' : 'text-slate-400')} />
        <span className={cn('min-w-0 flex-1 truncate text-[13px] font-bold', tone === 'waiting' ? 'text-emerald-900 dark:text-emerald-100' : 'text-slate-700 dark:text-slate-200')}>
          {c.name || 'Client'}
        </span>
        {tone === 'waiting' ? <ExpeditedMark expedite={c.expedite} /> : null}
        {showOwner && c.ownerName ? <span className="shrink-0 text-[11px] text-slate-400">{firstOf(c.ownerName)}&apos;s</span> : null}
      </span>
      <span className={cn('truncate pl-[22px] text-[11px]', tone === 'waiting' ? 'font-semibold text-emerald-700 dark:text-emerald-300' : 'text-slate-500 dark:text-slate-400')}>
        {sub}
      </span>
    </button>
  );
  return (
    <section className={cn(PANEL, 'p-4')} aria-label="Moved to CM today">
      <div className="mb-2.5 flex items-center gap-2">
        <UserCheck size={14} className="text-emerald-600 dark:text-emerald-300" />
        <h3 className="mb-0 text-[14px] font-bold text-slate-800 dark:text-slate-100">Moved to CM today</h3>
        <span className="ml-auto text-[12px] font-semibold tabular-nums text-slate-400">{assigned.length}</span>
      </div>
      {assigned.length ? (
        <div className="flex flex-col gap-1.5">
          {assigned.map((c) =>
            row(c, `With ${c.cmName || 'their Claims Manager'}${c.ptrsToFollow ? ' · PTRs to follow' : ''}${c.assignedAt ? ` · ${moment(c.assignedAt).format('h:mm A')}` : ''}`, 'assigned'),
          )}
        </div>
      ) : (
        <p className="mb-0 py-1 text-center text-[12.5px] text-slate-400">Clients who get their Claims Manager today land here.</p>
      )}
    </section>
  );
};

// Expedited (Nic 2026-10-26): by hand or for an Intent to File expiring
// within 90 days. A small mark; the hover says why.
export const ExpeditedMark = ({ expedite }) =>
  expedite ? (
    <span
      title={expedite.auto ? `Intent to File ${/expired/i.test(expedite.label) ? 'expired' : 'expires'} ${moment(expedite.itfExpires).format('MMM D, YYYY')}` : `Expedited: ${expedite.label}${expedite.note ? ` (${expedite.note})` : ''}`}
      className="inline-flex shrink-0 items-center gap-[3px] rounded-full bg-rose-500/10 px-[6px] py-px text-[11px] font-semibold text-rose-700 dark:text-rose-300"
    >
      <Zap size={10} /> Expedited
    </span>
  ) : null;

const OnboardingDesk = ({
  shown,
  now,
  viewerId,
  showOwner,
  canAct,
  labels,
  stallDays = 14,
  pinned = [],
  hideDone = false,
  // The rail's Scoreboard card folded (Nic 2026-11-03), saved with the person.
  scoreboardFolded = false,
  // Ready for CM off the All list (Nic 2026-10-27, saved with the person):
  // they wait on CM Assignment, so a long carried-over list stays out of the
  // way. The Ready for CM chip and Pinned still show them.
  hideReady = false,
  // The header search is on (Nic 2026-10-27): a hidden Ready for CM client
  // still shows, so a search never comes back empty for no visible reason.
  searching = false,
  meetingScope,
  onOpen,
  onOpenNotes = null,
  // The row's nudge icon (Nic 2026-10-27, like Sales' Signing Up): opens
  // the text composer with their nudge wording. null hides it.
  onNudge = null,
  // The org work calendar off the board payload (Nic 2026-11-01): on a
  // closed day the tile reads "Your move Monday" and a slate line says the
  // list is that day's. Nothing is locked: the list can be worked any time,
  // clients still move and the stall clock runs through the weekend.
  workCalendar = null,
  onOpenCalendar,
  assignedToCm = [],
}) => {
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState({ key: 'attention', dir: 'desc' });
  const setPinned = useSetPinned();
  // Pinned (Nic 2026-10-12): their own quick list, a star on each row.
  const pinnedSet = useMemo(() => new Set(pinned || []), [pinned]);
  const flipPin = (c) => setPinned.mutate({ onboardingId: c.onboardingId, pinned: !pinnedSet.has(c.onboardingId) });

  const working = useMemo(() => workingRowsOf(shown), [shown]);
  const ready = useMemo(() => (shown.readyForCm || []).map((c) => ({ ...c, column: 'readyForCm', stage: 'ready' })), [shown]);

  // Their to-dos, in the kit's words (personId = the onboardingId). One
  // query feeds the Today card and the mark beside each name (Nic
  // 2026-10-12): their own (sky) apart from the page's suggestions (violet).
  const todosQ = useOnboardingTodos(canAct);
  const todoRows = useMemo(
    () =>
      (todosQ.data?.todos || []).map((t) => ({ ...t, personId: t.onboardingId || null, personName: t.clientName || null })),
    [todosQ.data],
  );
  // Your move today: your own clients with a suggestion still open (the
  // Clients tab's number, the same rule).
  const needYou = clientIdsNeedingYou(working, viewerId, now, stallDays, todoRows);

  const counts = {
    new: working.filter((c) => c.stage === 'new').length,
    meet: working.filter((c) => c.stage === 'new' || c.stage === 'meet').length,
    booked: working.filter((c) => upcomingCall(c, now)).length,
    bookedToday: working.filter((c) => upcomingCall(c, now) && sameDay(c.meeting.startTime, now)).length,
  };
  const pinnedRows = [...working, ...ready].filter((c) => pinnedSet.has(c.onboardingId));
  const FILTERS = {
    // All is everyone on the desk (Nic 2026-10-26), Ready for CM included
    // unless they chose to hide them (2026-10-27).
    all: { label: 'Everyone', rows: hideReady && !searching ? working : [...working, ...ready] },
    today: { label: 'Your move today', rows: working.filter((c) => needYou.has(c.onboardingId)) },
    new: { label: 'New', rows: working.filter((c) => c.stage === 'new') },
    meet: { label: 'Needs first meeting', rows: working.filter((c) => c.stage === 'new' || c.stage === 'meet') },
    booked: { label: 'Booked', rows: working.filter((c) => upcomingCall(c, now)) },
    vaGov: { label: 'Setting up VA.gov', rows: working.filter((c) => c.stage === 'vaGov') },
    records: { label: 'Gathering records', rows: working.filter((c) => c.stage === 'records') },
    ptrs: { label: 'Waiting on PTRs', rows: working.filter((c) => c.stage === 'ptrs') },
    ready: { label: 'Ready for CM', rows: ready },
    pinned: { label: 'Pinned', rows: pinnedRows },
  };
  const pick = (f) => setFilter((cur) => (cur === f && f !== 'all' ? 'all' : f));

  // Default order: what needs them first. A call today, then new, then the
  // longest without movement.
  const attention = (c) => {
    const call = upcomingCall(c, now);
    if (call && sameDay(call.startTime, now)) return [0, new Date(call.startTime).getTime()];
    if (c.stage === 'new') return [1, -(c.movementDays ?? 0)];
    return [2, -(c.movementDays ?? 0)];
  };
  const rows = FILTERS[filter].rows.slice().sort((a, b) => {
    if (!SORTS[sort.key]) {
      const [x1, x2] = attention(a);
      const [y1, y2] = attention(b);
      return x1 - y1 || x2 - y2;
    }
    const r = SORTS[sort.key](a, b);
    return sort.dir === 'asc' ? -r : r;
  });

  const mineOnly = working.filter((c) => isOwnedBy(c.ownerUserId, viewerId));
  const suggestions = suggestionsFor(working, viewerId, now, stallDays);

  const todosFor = todosByPerson(todoRows, suggestions);
  const addTodo = useAddTodo();
  const updateTodo = useUpdateTodo();
  const removeTodo = useDeleteTodo();
  const savePrefs = useSaveOnboardingDisplayPrefs();

  const head = (key, label) => <SortHead sortKey={key} label={label} sort={sort} setSort={setSort} />;
  const chip = (key, label, n) => <FilterChip key={key} on={filter === key} label={label} n={n} onClick={() => pick(key)} />;
  const closedUntil = workCalendar && workCalendar.isWorkingToday === false ? workCalendar.nextOpenAt : null;
  const yourMove = yourMoveWords(closedUntil, 'Call, book, nudge or check in');

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
      <div className="flex min-w-0 flex-col gap-3">
        <ClosedTodayNote workCalendar={workCalendar} now={now}>
          Your move list is {yourMove.label.replace('Your move ', '')}&apos;s; work it any time. Clients still move and the stall
          clock keeps running.
        </ClosedTodayNote>
        <div className="grid grid-cols-2 gap-2.5 md:grid-cols-3 lg:grid-cols-6">
          <Counter
            value={FILTERS.today.rows.length}
            label={yourMove.label}
            hint={yourMove.hint}
            tone={FILTERS.today.rows.length ? 'text-amber-600 dark:text-amber-300' : ''}
            on={filter === 'today'}
            onClick={() => pick('today')}
          />
          <Counter value={counts.new} label="New" hint="Reach out: no call or text yet" on={filter === 'new'} onClick={() => pick('new')} />
          <Counter value={counts.meet} label="Needs first meeting" hint="Not booked, not met" on={filter === 'meet'} onClick={() => pick('meet')} />
          <Counter value={counts.booked} label="Booked" hint={counts.bookedToday ? `${counts.bookedToday} today` : 'Upcoming calls'} on={filter === 'booked'} onClick={() => pick('booked')} />
          {/* Waiting on PTRs (Nic 2026-10-26): everything else is in. */}
          <Counter
            value={FILTERS.ptrs.rows.length}
            label="Waiting on PTRs"
            hint="Everything else is in"
            on={filter === 'ptrs'}
            onClick={() => pick('ptrs')}
          />
          <Counter
            value={pinnedRows.length}
            label="Pinned"
            icon={<Star size={13} className="fill-amber-400 text-amber-400" />}
            hint={pinnedRows.length ? 'Your quick list' : 'Star a client to pin them'}
            on={filter === 'pinned'}
            onClick={() => pick('pinned')}
          />
        </div>

        <div className={cn(PANEL, 'overflow-hidden', filter !== 'all' && FILTERED_PANEL)}>
          <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3 dark:border-white/10">
            <h3 className="mb-0 mr-2 text-[14px] font-bold text-slate-800 dark:text-slate-100">
              {FILTERS[filter].label} <span className="font-semibold tabular-nums text-slate-400">· {rows.length}</span>
            </h3>
            {filter !== 'all' ? <FilteredPill onClear={() => setFilter('all')} /> : null}
            <div className="ml-auto flex flex-wrap gap-1.5">
              {chip('all', 'All', FILTERS.all.rows.length)}
              {chip('vaGov', 'Setting up VA.gov', FILTERS.vaGov.rows.length)}
              {chip('records', 'Gathering records', FILTERS.records.rows.length)}
              {/* Waiting on PTRs is the big tile above (Nic 2026-10-26). */}
              {/* Ready for CM is one chip in two parts (Nic 2026-10-27): the
                  filter on the left, and an eye on the right that hides them
                  from All (saved with the person). The chip always shows them. */}
              <div
                className={cn(
                  'inline-flex h-7 items-stretch overflow-hidden rounded-lg ring-1',
                  filter === 'ready' ? 'ring-slate-900 dark:ring-slate-100' : hideReady ? 'ring-amber-200 dark:ring-amber-500/30' : 'ring-slate-200 dark:ring-white/10',
                )}
              >
                <button
                  type="button"
                  aria-pressed={filter === 'ready'}
                  onClick={() => pick('ready')}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-2.5 text-[12px] font-semibold outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-400/60',
                    filter === 'ready'
                      ? 'bg-slate-900 text-white dark:!bg-slate-100 dark:!text-slate-900'
                      : 'bg-white text-slate-600 hover:bg-slate-50 dark:!bg-white/5 dark:text-slate-300 dark:hover:!bg-white/10',
                    hideReady && filter !== 'ready' && 'text-slate-400 dark:text-slate-500',
                  )}
                >
                  Ready for CM
                  <span className="tabular-nums opacity-70">{ready.length}</span>
                </button>
                <button
                  type="button"
                  aria-pressed={hideReady}
                  aria-label={hideReady ? 'Show Ready for CM on All' : 'Hide Ready for CM from All'}
                  title={hideReady ? 'Hidden from All. Click to show them again.' : 'Hide from All. This chip still shows them.'}
                  onClick={() => savePrefs.mutate({ hideReady: !hideReady })}
                  className={cn(
                    'inline-flex items-center border-l px-2 outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-400/60',
                    filter === 'ready'
                      ? 'border-white/20 bg-slate-900 text-white/70 hover:text-white dark:border-slate-900/15 dark:!bg-slate-100 dark:!text-slate-600 dark:hover:!text-slate-900'
                      : hideReady
                        ? 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-amber-500/30 dark:!bg-amber-500/15 dark:text-amber-300 dark:hover:!bg-amber-500/25'
                        : 'border-slate-200 bg-white text-slate-400 hover:bg-slate-50 hover:text-slate-700 dark:border-white/10 dark:!bg-white/5 dark:text-slate-400 dark:hover:!bg-white/10 dark:hover:text-white',
                  )}
                >
                  {hideReady ? <EyeOff size={13} strokeWidth={2.25} /> : <Eye size={13} strokeWidth={2.25} />}
                </button>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse text-[13px]">
              <thead className="bg-slate-50 dark:!bg-white/[0.03]">
                <tr>
                  {head('name', 'Client')}
                  <PlainHead>To-dos</PlainHead>
                  <PlainHead>Where they are</PlainHead>
                  {head('checklist', 'Checklist')}
                  {head('movement', 'Last movement')}
                  <PlainHead>Next</PlainHead>
                  <th className="w-24 py-2.5 pl-2 pr-4">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.length ? (
                  rows.map((c) => {
                    const st = STAGE[c.stage];
                    const next = nextOf(c, c.stage, labels, now);
                    const pr = c.progress || {};
                    const pct = pr.total ? Math.round(((pr.done || 0) / pr.total) * 100) : 0;
                    const stallOn = c.lastMovementAt ? moment(c.lastMovementAt).add(stallDays, 'days') : null;
                    // Nudge whenever the ball is in their court (Setting up
                    // VA.gov, Waiting on PTRs), on their own rows, when they
                    // can be texted (Nic 2026-10-27).
                    const canNudge =
                      !!onNudge &&
                      canAct &&
                      isOwnedBy(c.ownerUserId, viewerId) &&
                      nudgeWanted(c, now, stallDays) &&
                      !!c.phone &&
                      !c.smsOptedOut;
                    return (
                      <tr
                        key={c.onboardingId}
                        tabIndex={0}
                        onClick={() => onOpen(c.onboardingId)}
                        onKeyDown={(e) => {
                          // Enter or Space, like the button it acts as.
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onOpen(c.onboardingId);
                          }
                        }}
                        className="group/row cursor-pointer border-t border-slate-100 outline-none hover:bg-slate-50 focus-visible:bg-sky-50 dark:border-white/[0.06] dark:hover:!bg-white/[0.03] dark:focus-visible:!bg-sky-500/10"
                      >
                        <td className="py-2.5 pl-2 pr-3">
                          <div className="flex items-center gap-2">
                            <PinStar pinned={pinnedSet.has(c.onboardingId)} name={c.name} onFlip={() => flipPin(c)} />
                            <span
                              title={showOwner ? c.ownerName || 'No specialist' : undefined}
                              className={cn(
                                'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold',
                                showOwner ? 'bg-slate-800 text-white dark:!bg-slate-200 dark:!text-slate-900' : 'bg-sky-500/10 text-sky-700 dark:text-sky-300',
                              )}
                            >
                              {initialsOf(showOwner ? c.ownerName : c.name)}
                            </span>
                            <div className="min-w-0">
                              <div className="flex min-w-0 items-baseline gap-1.5">
                                <span className="truncate font-semibold text-slate-800 dark:text-slate-100">{c.name || 'Unnamed client'}</span>
                                {/* They came in hot (Nic 2026-10-23): for good. */}
                                <HotSeatMark seat={c.hotSeat} />
                                <ExpeditedMark expedite={c.expedite} />
                                {/* Back from Stalled and nobody has worked with them
                                    since (Nic 2026-10-12). */}
                                {c.cameBack ? (
                                  <span
                                    title={c.reengagedAt ? `Back from Stalled ${moment(c.reengagedAt).format('MMM D')}` : 'Back from Stalled'}
                                    className="shrink-0 text-[12px] font-medium italic tracking-[0.01em] text-sky-600 dark:text-sky-300"
                                  >
                                    re-engaged
                                  </span>
                                ) : null}
                                {/* New from the client since you last opened them
                                    (client activity, Nic 2026-10-23): a mark, the
                                    updates on hover, never the whole update in the row. */}
                                <ActivityMark name={c.name} activity={c.newActivity} labels={labels} now={now} />
                              </div>
                              <div className="truncate text-[12px] text-slate-400">
                                {showOwner && c.ownerName ? `${firstOf(c.ownerName)}'s · ` : ''}
                                {c.signedAt ? `signed ${moment(c.signedAt).format('MMM D')}` : ''}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5">
                          <TodoMark mine={todosFor.mine.get(c.onboardingId)} suggested={todosFor.suggested.get(c.onboardingId)} />
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5">
                          <span className={cn('inline-flex rounded-full px-2.5 py-0.5 text-[12px] font-semibold', st.cls)}>{st.label}</span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5">
                          <span className="inline-flex items-center gap-2">
                            <span className="block h-1.5 w-16 overflow-hidden rounded-full bg-slate-100 dark:!bg-white/10">
                              <span className="block h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
                            </span>
                            <span className="text-[12px] font-semibold tabular-nums text-slate-500 dark:text-slate-400">
                              {pr.done ?? 0}/{pr.total ?? 0}
                            </span>
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5">
                          <span
                            title={
                              stallOn
                                ? `Stalled ${stallOn.format('ddd, MMM D')} with no sign of life from them${
                                    c.lastTouchAt && moment(c.lastTouchAt).isAfter(c.lastMovementAt)
                                      ? `. Last tried ${moment(c.lastTouchAt).format('MMM D')}`
                                      : ''
                                  }`
                                : undefined
                            }
                            className={cn('inline-flex items-center gap-1.5 font-semibold', moveTone(c.movementDays))}
                          >
                            <span className={cn('h-2 w-2 rounded-full', moveDot(c.movementDays))} />
                            {moveText(c.movementDays)}
                          </span>
                        </td>
                        <td className={cn('max-w-[16rem] truncate px-3 py-2.5', next.strong ? 'font-semibold text-slate-800 dark:text-slate-100' : 'text-slate-600 dark:text-slate-300')}>
                          {next.text}
                          {next.legacy ? <LegacyTag className="ml-1.5" /> : null}
                        </td>
                        <td className="py-2.5 pl-2 pr-4">
                          <div className="flex items-center justify-end gap-1.5">
                            {canNudge ? (
                              <button
                                type="button"
                                aria-label={`Nudge ${c.name}`}
                                title="Send their nudge text"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onNudge(c);
                                }}
                                onKeyDown={(e) => e.stopPropagation()}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-teal-50 text-teal-600 shadow-[0_1px_2px_rgba(15,23,42,0.06)] outline-none ring-1 ring-teal-200/80 transition-transform duration-150 hover:-translate-y-px hover:bg-teal-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400/60 active:scale-95 dark:!bg-teal-400/10 dark:text-teal-300 dark:ring-teal-400/25 dark:hover:!bg-teal-400/20"
                              >
                                <MessageCircleHeart size={15} strokeWidth={2.25} />
                              </button>
                            ) : null}
                            {/* Onboarding wrote a note (Nic 2026-10-12): opens them on Notes. */}
                            {c.hasNotes ? (
                              <NotesButton name={c.name} onClick={() => (onOpenNotes || onOpen)(c.onboardingId)} />
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-[13px] text-slate-400">
                      {filter === 'all'
                        ? 'Nobody here yet. New signups land here.'
                        : filter === 'pinned'
                          ? 'Nobody pinned yet. Hover a client and tap the star to keep them here.'
                          : 'Nobody here right now.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Schedule first, then the to-dos (Nic 2026-10-12). */}
      <aside className="flex flex-col gap-4">
        <ScheduleCard
          now={now}
          scope={meetingScope}
          apiBase="/client-onboarding"
          onOpenMeeting={(m) => m.onboardingId && onOpen(m.onboardingId)}
          onOpenCalendar={onOpenCalendar}
          showOwner={showOwner}
          fallbackName="Client"
          fallbackEvent="Onboarding call"
        />
        {/* The Scoreboard (Nic 2026-11-03): FOTCs by specialist, today and
            this week, above the checklist; folds, and the fold is saved. */}
        <ScoreboardCard team="onboarding" enabled={canAct} folded={scoreboardFolded} onFold={(next) => savePrefs.mutate({ scoreboardFolded: next })} />
        <TodayCard
          suggestions={suggestions}
          kinds={KIND_OF}
          todos={todoRows}
          adding={addTodo.isLoading}
          onAdd={({ personId, ...rest }, opts) => addTodo.mutate({ ...rest, onboardingId: personId || null }, opts)}
          onUpdate={(x) => updateTodo.mutate(x)}
          onRemove={(x) => removeTodo.mutate(x)}
          canAct={canAct}
          people={mineOnly
            .map((c) => ({ id: c.onboardingId, name: c.name }))
            .sort((a, b) => String(a.name).localeCompare(String(b.name)))}
          noun="client"
          idPrefix="co"
          onOpen={onOpen}
          now={now}
          savedHideDone={hideDone}
          onSaveHideDone={(next) => savePrefs.mutate({ hideDone: next })}
        />
        <MovedToCm assigned={assignedToCm} showOwner={showOwner} onOpen={onOpen} />
      </aside>
    </div>
  );
};

export default OnboardingDesk;